import React, { useRef, useMemo, useState, useCallback } from 'react';
import { Canvas, useFrame, useThree, extend } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';
import { Badge } from './ui/badge';
import { X, RotateCcw } from 'lucide-react';

// ── Flat card in 3D space ──
function PlatformCard({ node, position, color, isSelected, isHovered, onSelect, onHover }) {
  const groupRef = useRef();

  // Billboard: always face camera
  useFrame(({ camera }) => {
    if (groupRef.current) {
      groupRef.current.quaternion.copy(camera.quaternion);
    }
  });

  const opacity = node.used ? 1 : 0.4;

  return (
    <group ref={groupRef} position={position}>
      {/* Card background */}
      <mesh
        onClick={(e) => { e.stopPropagation(); onSelect(node); }}
        onPointerOver={(e) => { e.stopPropagation(); onHover(node); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { onHover(null); document.body.style.cursor = 'default'; }}
      >
        <planeGeometry args={[2.2, 0.7]} />
        <meshBasicMaterial
          color={isSelected ? '#ffffff' : (isHovered ? '#f8fafc' : '#ffffff')}
          transparent opacity={opacity * 0.95}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Left color stripe */}
      <mesh position={[-1.05, 0, 0.001]}>
        <planeGeometry args={[0.08, 0.7]} />
        <meshBasicMaterial color={color} side={THREE.DoubleSide} />
      </mesh>

      {/* Selection ring */}
      {isSelected && (
        <mesh>
          <planeGeometry args={[2.35, 0.85]} />
          <meshBasicMaterial color={color} transparent opacity={0.3} side={THREE.DoubleSide} />
        </mesh>
      )}

      {/* Status dot */}
      <mesh position={[0.9, 0.2, 0.002]}>
        <circleGeometry args={[0.06, 16]} />
        <meshBasicMaterial color={node.used ? '#22c55e' : '#d1d5db'} side={THREE.DoubleSide} />
      </mesh>

      {/* HTML label overlay */}
      <Html position={[0, 0, 0.01]} transform style={{ pointerEvents: 'none' }} distanceFactor={5}>
        <div style={{ width: '120px', textAlign: 'left', opacity, fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: '#0f172a', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {node.short || node.name}
          </div>
          {node.vendor_name && (
            <div style={{ fontSize: '7px', color: '#64748b', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {node.vendor_name}
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}

// ── Category label floating in space ──
function CategoryLabel({ name, color, position }) {
  const ref = useRef();
  useFrame(({ camera }) => { if (ref.current) ref.current.quaternion.copy(camera.quaternion); });
  return (
    <group ref={ref} position={position}>
      <Html transform style={{ pointerEvents: 'none' }} distanceFactor={8}>
        <div style={{
          fontSize: '10px', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
          color, fontFamily: 'system-ui', whiteSpace: 'nowrap', textShadow: '0 0 8px rgba(255,255,255,0.5)',
        }}>
          {name}
        </div>
      </Html>
    </group>
  );
}

// ── Integration line ──
function IntegrationLine({ from, to, color = '#cbd5e1' }) {
  const geo = useMemo(() => {
    const mid = [(from[0] + to[0]) / 2, Math.max(from[1], to[1]) + 1.5, (from[2] + to[2]) / 2];
    const curve = new THREE.QuadraticBezierCurve3(
      new THREE.Vector3(...from), new THREE.Vector3(...mid), new THREE.Vector3(...to)
    );
    return new THREE.BufferGeometry().setFromPoints(curve.getPoints(20));
  }, [from, to]);

  const mat = useMemo(() => new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.15 }), [color]);
  const lineObj = useMemo(() => new THREE.Line(geo, mat), [geo, mat]);

  return <primitive object={lineObj} />;
}

// ── Main 3D Component ──
export default function Visualization3D({ stackData, onClose }) {
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);

  const nodes = stackData?.nodes || [];
  const links = stackData?.integrations || [];

  // Position nodes in 3D by category clusters
  const { positioned, catPositions } = useMemo(() => {
    const catGroups = {};
    nodes.forEach(n => {
      const cat = n.category_name || 'Other';
      if (!catGroups[cat]) catGroups[cat] = { color: n.category_color || '#6366f1', nodes: [] };
      catGroups[cat].nodes.push(n);
    });
    const catEntries = Object.entries(catGroups);
    const cols = Math.ceil(Math.sqrt(catEntries.length));

    const result = [];
    const catPos = [];

    catEntries.forEach(([catName, cat], catIdx) => {
      const row = Math.floor(catIdx / cols);
      const col = catIdx % cols;
      const baseX = (col - cols / 2 + 0.5) * 7;
      const baseZ = (row - Math.ceil(catEntries.length / cols) / 2 + 0.5) * 7;

      catPos.push({ name: catName, color: cat.color, position: [baseX, 3.5, baseZ] });

      cat.nodes.forEach((node, i) => {
        const perRow = Math.ceil(Math.sqrt(cat.nodes.length));
        const nr = Math.floor(i / perRow);
        const nc = i % perRow;
        const x = baseX + (nc - perRow / 2 + 0.5) * 2.5;
        const z = baseZ + (nr - Math.ceil(cat.nodes.length / perRow) / 2 + 0.5) * 1.2;
        const y = 0;
        result.push({ ...node, pos: [x, y, z] });
      });
    });

    return { positioned: result, catPositions: catPos };
  }, [nodes]);

  // Build link pairs
  const linkPairs = useMemo(() => {
    return links.slice(0, 60).map(link => {
      const sid = typeof link.source === 'object' ? link.source.id : link.source;
      const tid = typeof link.target === 'object' ? link.target.id : link.target;
      const sn = positioned.find(n => n.id === sid);
      const tn = positioned.find(n => n.id === tid);
      if (!sn || !tn) return null;
      return { from: sn.pos, to: tn.pos };
    }).filter(Boolean);
  }, [positioned, links]);

  const info = selected || hovered;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-b from-slate-100 to-white" data-testid="3d-visualization">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-sm border-b">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>3D Stack View</h2>
          <span className="text-xs text-muted-foreground">{nodes.filter(n => n.used).length} active / {nodes.length} total platforms</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">Drag to rotate · Scroll to zoom · Click nodes for details</span>
          <button onClick={onClose} className="ml-3 p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="close-3d">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Node info panel */}
      {info && (
        <div className="absolute bottom-4 left-4 z-10 bg-white border rounded-xl shadow-lg p-4 max-w-xs" data-testid="3d-node-info">
          <div className="flex items-start gap-2">
            <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: info.category_color || '#6366f1' }} />
            <div>
              <h3 className="text-sm font-bold">{info.short || info.name}</h3>
              {info.vendor_name && <p className="text-xs text-muted-foreground">{info.vendor_name}</p>}
              <div className="flex gap-1.5 mt-1.5">
                <Badge variant="outline" className={`text-[9px] ${info.used ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'text-muted-foreground'}`}>
                  {info.used ? 'Active' : 'Not Deployed'}
                </Badge>
                <Badge variant="outline" className="text-[9px]">{info.category_name}</Badge>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 right-4 z-10 bg-white/80 backdrop-blur-sm border rounded-lg px-3 py-2 flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Active</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" /> Gap</span>
        <span className="flex items-center gap-1"><span className="w-4 h-px bg-slate-300" /> Integration</span>
      </div>

      {/* Canvas */}
      <Canvas camera={{ position: [18, 14, 18], fov: 45 }} style={{ background: 'transparent' }}>
        <ambientLight intensity={0.8} />
        <directionalLight position={[10, 20, 10]} intensity={0.5} />

        <OrbitControls
          enableDamping dampingFactor={0.05}
          minDistance={5} maxDistance={50}
          maxPolarAngle={Math.PI / 2.2}
        />

        {/* Ground grid */}
        <gridHelper args={[40, 40, '#e2e8f0', '#f1f5f9']} position={[0, -0.5, 0]} />

        {/* Category labels */}
        {catPositions.map(cat => (
          <CategoryLabel key={cat.name} name={cat.name} color={cat.color} position={cat.position} />
        ))}

        {/* Platform cards */}
        {positioned.map(node => (
          <PlatformCard
            key={node.id}
            node={node}
            position={node.pos}
            color={node.category_color || '#6366f1'}
            isSelected={selected?.id === node.id}
            isHovered={hovered?.id === node.id}
            onSelect={setSelected}
            onHover={setHovered}
          />
        ))}

        {/* Integration arcs */}
        {linkPairs.map((lp, i) => (
          <IntegrationLine key={i} from={lp.from} to={lp.to} />
        ))}
      </Canvas>
    </div>
  );
}
